<template>
	<div class="container">
		<div class="page page-inventory">
			<!-- Header -->
			<div class="page-header d-flex justify-content-between">
				<h1 class="page-title">{{ $t('Seller Credit Topup Monitoring') }}</h1>
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>

			<!-- Summary Scoreboard -->
			<div class="inventory-summaries row">
				<div class="col-md-4">
					<div class="box box--primary mh-120">
						<div class="m-2">
							<h4
								class="text-primary font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingScoreboard }">
								<span> Rp {{ totalTopupDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('Total Topup') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-4">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-success font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingScoreboard }">
								<span> Rp {{ paidTopupDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-check-circle"></i> {{ $t('Paid Topup') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-4">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-danger font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingScoreboard }">
								<span> Rp {{ unpaidTopupDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-clock"></i> {{ $t('Unpaid Topup') }}</span>
						</div>
					</div>
				</div>
			</div>
			<hr />
			<div class="row">
				<div class="col-md-6">
					<div class="form-group">
						<input type="text" class="form-control" placeholder="Search by Seller Name, Email, or Topup ID" v-model="keyword" @input="onKeywordChange">
					</div>
				</div>
				<div class="mb-4 col-md-6 text-right">
					<div class="d-flex justify-content-end">
						<!-- Export -->
						<button
							type="button"
							class="btn btn-sm btn-primary"
							:class="{ 'is-loading': isExporting }"
							@click="exportTopup">
							<i class="material-icons">save_alt</i>
							<span class="ml-2">{{ $t('Export') }}</span>
						</button>
						<select class="form-control ml-2" v-model="statusSelected" style="width: 150px;">
							<option value="all">{{ $t('All Status') }}</option>
							<option value="paid">{{ $t('Paid') }}</option>
							<option value="pending">{{ $t('Unpaid') }}</option>
						</select>
					</div>
				</div>
			</div>

			<!-- Main Table -->
			<div class="card card-body border-0">
				<div class="topup-table table-responsive">
					<table class="table table-striped table-hover">
						<thead>
							<tr>
								<th width="5%">{{ $t('No') }}.</th>
								<th width="20%">{{ $t('Member') }}</th>
								<th width="15%">{{ $t('Topup ID') }}</th>
								<th width="15%">{{ $t('Nominal') }}</th>
								<th width="10%">{{ $t('Status') }}</th>
								<th width="15%">{{ $t('Created At') }}</th>
								<th width="10%">{{ $t('Wallet ID') }}</th>
								<th width="10%">{{ $t('Paid At') }}</th>
							</tr>
						</thead>
						<tbody>
							<template v-for="(topup, index) in topups">
								<tr :key="topup._id">
									<!-- Index -->
									<td class="text-right">{{ index + 1 }}.</td>

									<!-- Member -->
									<td>
										<div class="font-weight-bold">{{ topup.user ? topup.user.name : '-' }}</div>
										<div class="text-muted small">{{ topup.user ? topup.user.email : '-' }}</div>
									</td>

									<!-- Topup ID -->
									<td>
										<span class="text-bold text-primary">#{{ topup.topup_id }}</span>
									</td>

									<!-- Nominal -->
									<td class="text-right">
										<span class="font-weight-bold">Rp {{ formatNumber(topup.amount) }}</span>
									</td>

									<!-- Status -->
									<td class="text-center">
										<mark :class="`order-status status-${getStatusClass(topup.status)}`">
											<span>{{ getStatusLabel(topup.status) }}</span>
										</mark>
									</td>

									<!-- Created At -->
									<td class="text-center">
										<span>{{ formatDate(topup.created_at) }}</span>
									</td>

									<!-- Wallet ID -->
									<td>
										<span class="small">TP-{{ topup.topup_id }}</span>
									</td>

									<!-- Paid At -->
									<td class="text-center">
										<span>{{ topup.status === 'paid' ? formatDate(topup.updated_at) : '-' }}</span>
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>

			<!-- Table Loader -->
			<div
				class="table-fetch-status"
				v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Table Empty -->
			<div
				class="list-empty border-0"
				v-if="topups.length === 0 && isFetching === false">
				<span><i class="material-icons md-48 mr-2">error_outline</i></span>
				<span v-if="keyword === '' && statusSelected === 'all'">There is no topup data yet.</span>
				<span v-else>No topup was found matching your criteria.</span>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import topupApi from '@/api/topup';
import {
	formatNumberPlain,
	downloadFile,
} from '@/lib/helper';

const defaultFilterDate = {
	start: dayjs().startOf('month').format('YYYY-MM-DD'),
	end: dayjs().endOf('month').format('YYYY-MM-DD'),
};

export default {
	name: 'AdminSellerTopupMonitoring',
	components: {
		Daterangepicker,
	},
	data() {
		return {
			keyword: '',
			limit: 25,
			page: 1,
			sort_by: 'created_at',
			sort: 'desc',
			topups: [],
			isFetching: false,
			isExporting: false,
			isAll: false,
			filterDate: defaultFilterDate,
			isFetchingScoreboard: false,
			statusSelected: 'all',
			scoreboard: {
				total_topup: 0,
				paid_topup: 0,
				unpaid_topup: 0,
			},
		};
	},
	methods: {
		resetTopups() {
			this.topups = [];
			this.isAll = false;
			this.page = 1;
		},
		fetchTopups(reset = true, includeDate = true) {
			this.isFetching = true;
			if (reset) {
				this.resetTopups();
			}
			const params = this.getFetchParams(includeDate);
			const callback = (response) => {
				const topups = response.data || [];
				topups.forEach((topup) => {
					this.topups.push(topup);
				});
				this.page++;
				if (topups.length < this.limit) {
					this.isAll = true;
				}
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			topupApi.getAdminList(params, callback, errorCallback);
		},
		fetchScoreboard() {
			this.isFetchingScoreboard = true;
			const params = this.getScoreboardParams();
			const callback = (response) => {
				this.scoreboard = response.data || {
					total_topup: 0,
					paid_topup: 0,
					unpaid_topup: 0,
				};
				this.isFetchingScoreboard = false;
			};
			const errorCallback = () => {
				this.isFetchingScoreboard = false;
			};
			topupApi.getAdminScoreboard(params, callback, errorCallback);
		},
		init() {
			this.initFilterDate();
			this.refresh();
		},
		refresh() {
			this.fetchScoreboard();
			this.fetchTopups();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = { ...this.$route.query };
			query.since = since;
			query.until = until;
			this.$router.push({ query });
			this.refresh();
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchTopups(true, false);
				self.fetchScoreboard();
			}, 1000);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchTopups(false, false);
				}
			}
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		getFetchParams(includeDate = true) {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.statusSelected !== 'all') {
				params.status = this.statusSelected;
			}

			if (includeDate) {
				params.since = startDate;
				params.until = endDate;
			}

			return params;
		},
		getScoreboardParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				since: startDate,
				until: endDate,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.statusSelected !== 'all') {
				params.status = this.statusSelected;
			}

			return params;
		},
		formatNumber(number) {
			return formatNumberPlain(number || 0);
		},
		formatDate(dateString) {
			if (!dateString) return '-';
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			return this.moment.utc(dateString).tz(timezone).format(format);
		},
		getStatusClass(status) {
			const statusMap = {
				paid: 'success',
				pending: 'danger',
				rejected: 'secondary',
			};
			return statusMap[status] || 'secondary';
		},
		getStatusLabel(status) {
			const labelMap = {
				paid: 'Paid',
				pending: 'Unpaid',
				rejected: 'Cancelled',
			};
			return labelMap[status] || status;
		},
		exportTopup() {
			this.isExporting = true;
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				sort_by: this.sort_by,
				sort: this.sort,
				since: startDate,
				until: endDate,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.statusSelected !== 'all') {
				params.status = this.statusSelected;
			}

			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					const message = 'No topup data to export';
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export Topup',
						text: message,
					});
				}
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			topupApi.exportAdmin(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			filterDatePresets: 'filterDatePresets',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		totalTopupDisplay() {
			return formatNumberPlain(this.scoreboard.total_topup);
		},
		paidTopupDisplay() {
			return formatNumberPlain(this.scoreboard.paid_topup);
		},
		unpaidTopupDisplay() {
			return formatNumberPlain(this.scoreboard.unpaid_topup);
		},
	},
	watch: {
		user() {
			this.init();
		},
		statusSelected() {
			this.refresh();
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.mh-120 {
	min-height: 120px;
}
</style>
